import { inResiOrmStore } from "@superbright/indexeddb-orm";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { queryKeys } from "api/config";
import { queryClient } from "utils/query";

import {
  fetchVisitorSession,
  setVisitorUuidCookie,
} from "./fetchVisitorSession";

export const useVisitorSession = (slug: string | undefined) => {
  // Delay query enabling until after the first render so the queryFn doesn't
  // run during the hydration commit phase (defence-in-depth).
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  // Capture visitor_uuid (vid) from query params once on mount (before any URL cleanup)
  const [vidFromUrl] = useState(() =>
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("vid")
      : null
  );

  // Always strip vid from the URL so it isn't shared between users
  useEffect(() => {
    if (vidFromUrl) {
      const url = new URL(window.location.href);
      url.searchParams.delete("vid");
      window.history.replaceState({}, "", url.toString());
    }
  }, [vidFromUrl]);

  // When a vid arrives via email link, apply it directly and immediately.
  // The query's queryFn is gated behind `enabled: mounted`, so on a warm
  // browser (SSR seeds the cache from the existing cookie) it never re-runs and
  // the vid override is silently dropped. Writing here does not depend on that.
  useEffect(() => {
    if (!vidFromUrl) return;

    // 1) Our own identity: IndexedDB + react-query cache + cookie (for SSR).
    void inResiOrmStore.setVisitorUUID(vidFromUrl);
    queryClient.setQueryData(queryKeys.visitorUUID(), vidFromUrl);
    setVisitorUuidCookie(vidFromUrl);

    // 2) PostHog and Mixpanel each keep their own distinct_id and won't
    // re-identify an already-identified user, so we reset() then identify() to
    // force the handoff id onto both. Poll each singleton until it has
    // initialised (avoids importing the analytics-ready module into this root
    // hook's graph).
    let cancelled = false;

    const reconcilePosthog = async (attempt = 0) => {
      if (cancelled) return;
      try {
        const posthog = (await import("posthog-js")).default as unknown as {
          __loaded?: boolean;
          get_distinct_id?: () => string | undefined;
          reset?: () => void;
          identify?: (id: string) => void;
        };
        if (posthog?.__loaded) {
          if (posthog.get_distinct_id?.() !== vidFromUrl) {
            posthog.reset?.();
            posthog.identify?.(vidFromUrl);
          }
          return;
        }
      } catch {
        // posthog not available (e.g. analytics disabled) — nothing to sync.
        return;
      }
      if (attempt < 100 && !cancelled) {
        window.setTimeout(() => void reconcilePosthog(attempt + 1), 100);
      }
    };

    const reconcileMixpanel = async (attempt = 0) => {
      if (cancelled) return;
      try {
        const mixpanel = (await import("mixpanel-browser"))
          .default as unknown as {
          get_distinct_id?: () => string | undefined;
          reset?: () => void;
          identify?: (id: string) => void;
        };
        // Mixpanel throws from get_distinct_id() until init() has run, so a
        // returned id means it is ready.
        const current = mixpanel?.get_distinct_id?.();
        if (current !== undefined) {
          if (current !== vidFromUrl) {
            mixpanel.reset?.();
            mixpanel.identify?.(vidFromUrl);
          }
          return;
        }
      } catch {
        // not initialised yet (or disabled) — retry below.
      }
      if (attempt < 100 && !cancelled) {
        window.setTimeout(() => void reconcileMixpanel(attempt + 1), 100);
      }
    };

    void reconcilePosthog();
    void reconcileMixpanel();

    return () => {
      cancelled = true;
    };
  }, [vidFromUrl]);

  // vidFromUrl is intentionally NOT part of the queryKey: visitorUUID() is the
  // canonical identity key shared with seedCache/useBootstrap/fetchVisitorSession.
  // The vid is an override applied into that same key, not a separate cache entry.
  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  const storedQuery = useQuery({
    queryKey: queryKeys.visitorUUID(),
    // Single ordered source of truth: if a vid arrived via URL, apply it to
    // IndexedDB and return it; otherwise read whatever is stored. This keeps
    // the query cache authoritative so the cookie-mirror effect below never
    // overwrites the fresh vid with a stale read.
    queryFn: async () => {
      if (vidFromUrl) {
        await inResiOrmStore.setVisitorUUID(vidFromUrl);
        return vidFromUrl;
      }
      return inResiOrmStore.getVisitorUUID();
    },
    enabled: mounted,
    staleTime: Infinity,
    cacheTime: Infinity,
    // When a vid arrives via URL it must override whatever identity is already
    // in play — including a cache the SSR layer seeded from the existing cookie
    // (seedCache bumps dataUpdateCount, so the observer would otherwise serve
    // the stale value and skip queryFn). Force the queryFn to run in that case.
    refetchOnMount: vidFromUrl ? "always" : true,
  });

  // Sync visitor_uuid to cookie on every page load for SSR. A vid from the URL
  // is authoritative and must win over the value the SSR layer seeded from the
  // existing cookie — otherwise this effect can re-write the stale seeded id
  // right after the handoff effect wrote the vid (the seeded value is present
  // in this render's closure before setQueryData re-renders).
  useEffect(() => {
    const resolved = vidFromUrl ?? storedQuery.data;
    if (resolved) {
      setVisitorUuidCookie(resolved);
    }
  }, [vidFromUrl, storedQuery.data]);

  const sessionQuery = useQuery({
    queryKey: queryKeys.visitorSession(slug ?? ""),
    queryFn: () => fetchVisitorSession(slug!),
    // Never mint a fresh session when a vid handoff is in play.
    enabled:
      !!slug &&
      !vidFromUrl &&
      storedQuery.isSuccess &&
      storedQuery.data === null,
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  // vid wins over any seeded/stored value so the whole app (AnalyticsProvider's
  // visitor_uuid, bootstrap, engagement) uses the handoff id immediately.
  return vidFromUrl ?? storedQuery.data ?? sessionQuery.data ?? undefined;
};

/**
 * Visitor id this browser holds: `undefined` while looking up, `null` if none.
 * Observes the entry `useVisitorSession` fills; never fetches on its own.
 */
export const useStoredVisitorId = (): string | null | undefined => {
  const { data } = useQuery({
    queryKey: queryKeys.visitorUUID(),
    queryFn: () => inResiOrmStore.getVisitorUUID(),
    enabled: false,
    staleTime: Infinity,
    cacheTime: Infinity,
  });
  return data;
};
